import { line } from '@visx/shape';
import { localPoint } from '@visx/event';

const DEFAULT_SAMPLES_PER_SEGMENT = 16;

/**
 * @typedef {'line' | 'cubic'} PathSegmentType
 */

/**
 * @typedef {Object} LinePathSegment
 * @property {PathSegmentType} type
 * @property {number} x0
 * @property {number} y0
 * @property {number} [x1]
 * @property {number} [y1]
 * @property {number} [x2]
 * @property {number} [y2]
 * @property {number} [x3]
 * @property {number} [y3]
 */

/**
 * @typedef {Object} ChartPoint
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {Object} LineSeries
 * @property {string|number} id
 * @property {Array<*>} data
 */

/**
 * @typedef {Object} ClosestLineMatch
 * @property {string|number} id
 * @property {LineSeries} series
 * @property {number} distance
 * @property {ChartPoint} snapPoint
 */

/**
 * Build a d3 line generator configured the same way as @visx/shape LinePath.
 *
 * @param {Object} config
 * @param {Function} config.x
 * @param {Function} config.y
 * @param {import('d3-shape').CurveFactory | import('d3-shape').CurveFactoryLineOnly} [config.curve]
 * @param {Function} [config.defined]
 */
export function createLinePathGenerator({ x, y, curve, defined }) {
  return line({ x, y, curve, defined });
}

/**
 * Collect drawable segments for a line path using the same curve interpolation
 * that @visx/shape LinePath renders. Works without DOM APIs, so it is safe in
 * tests and SSR.
 *
 * @param {Object} config
 * @param {Array<*>} config.data
 * @param {Function} config.x
 * @param {Function} config.y
 * @param {import('d3-shape').CurveFactory | import('d3-shape').CurveFactoryLineOnly} [config.curve]
 * @param {Function} [config.defined]
 * @returns {LinePathSegment[]}
 */
export function collectLinePathSegments({ data = [], x, y, curve, defined }) {
  if (!data.length || !x || !y) {
    return [];
  }

  /** @type {LinePathSegment[]} */
  const segments = [];
  /** @type {ChartPoint | null} */
  let current = null;

  const context = {
    moveTo(px, py) {
      current = { x: px, y: py };
    },
    lineTo(px, py) {
      if (!current) {
        current = { x: px, y: py };
        return;
      }

      segments.push({
        type: 'line',
        x0: current.x,
        y0: current.y,
        x1: px,
        y1: py,
      });
      current = { x: px, y: py };
    },
    bezierCurveTo(x1, y1, x2, y2, px, py) {
      if (!current) {
        current = { x: px, y: py };
        return;
      }

      segments.push({
        type: 'cubic',
        x0: current.x,
        y0: current.y,
        x1,
        y1,
        x2,
        y2,
        x3: px,
        y3: py,
      });
      current = { x: px, y: py };
    },
    closePath() {},
  };

  createLinePathGenerator({ x, y, curve, defined }).context(context)(data);

  return segments;
}

function cubicBezierPoint(t, x0, y0, x1, y1, x2, y2, x3, y3) {
  const u = 1 - t;
  const tt = t * t;
  const uu = u * u;
  const uuu = uu * u;
  const ttt = tt * t;

  return {
    x: uuu * x0 + 3 * uu * t * x1 + 3 * u * tt * x2 + ttt * x3,
    y: uuu * y0 + 3 * uu * t * y1 + 3 * u * tt * y2 + ttt * y3,
  };
}

/**
 * @returns {{ distance: number, snapPoint: ChartPoint }}
 */
function distanceToLineSegment(point, segment, samplesPerSegment) {
  const { x: px, y: py } = point;

  if (segment.type === 'line') {
    const { x0, y0, x1, y1 } = segment;
    const dx = x1 - x0;
    const dy = y1 - y0;
    const lengthSquared = dx * dx + dy * dy;

    if (lengthSquared === 0) {
      const distance = Math.hypot(px - x0, py - y0);
      return { distance, snapPoint: { x: x0, y: y0 } };
    }

    const t = Math.max(
      0,
      Math.min(1, ((px - x0) * dx + (py - y0) * dy) / lengthSquared)
    );
    const snapPoint = { x: x0 + t * dx, y: y0 + t * dy };

    return {
      distance: Math.hypot(px - snapPoint.x, py - snapPoint.y),
      snapPoint,
    };
  }

  const { x0, y0, x1, y1, x2, y2, x3, y3 } = segment;
  let closestDistance = Infinity;
  /** @type {ChartPoint} */
  let snapPoint = { x: x0, y: y0 };

  for (let i = 0; i <= samplesPerSegment; i += 1) {
    const t = i / samplesPerSegment;
    const candidate = cubicBezierPoint(t, x0, y0, x1, y1, x2, y2, x3, y3);
    const distance = Math.hypot(px - candidate.x, py - candidate.y);

    if (distance < closestDistance) {
      closestDistance = distance;
      snapPoint = candidate;
    }
  }

  return { distance: closestDistance, snapPoint };
}

/**
 * @typedef {Object} LineProximityIndexEntry
 * @property {string|number} id
 * @property {LineSeries} series
 * @property {LinePathSegment[]} segments
 * @property {ChartPoint[]} points isolated data points with no drawable segments
 */

/**
 * @typedef {Object} LineProximityIndex
 * @property {LineProximityIndexEntry[]} entries
 */

function collectDefinedDataPoints({ data = [], x, y, defined }) {
  return data.reduce((points, datum, index) => {
    if (defined && !defined(datum, index, data)) {
      return points;
    }

    const px = x(datum, index, data);
    const py = y(datum, index, data);

    if (px == null || py == null || Number.isNaN(px) || Number.isNaN(py)) {
      return points;
    }

    points.push({ x: px, y: py });
    return points;
  }, []);
}

/**
 * @returns {{ distance: number, snapPoint: ChartPoint | null }}
 */
function distanceToPathSegments(
  point,
  segments,
  samplesPerSegment,
  points = []
) {
  let closestDistance = Infinity;
  /** @type {ChartPoint | null} */
  let snapPoint = null;

  segments.forEach((segment) => {
    const result = distanceToLineSegment(point, segment, samplesPerSegment);

    if (result.distance < closestDistance) {
      closestDistance = result.distance;
      snapPoint = result.snapPoint;
    }
  });

  points.forEach((dataPoint) => {
    const distance = Math.hypot(point.x - dataPoint.x, point.y - dataPoint.y);

    if (distance < closestDistance) {
      closestDistance = distance;
      snapPoint = dataPoint;
    }
  });

  if (snapPoint == null) {
    return { distance: Infinity, snapPoint: null };
  }

  return { distance: closestDistance, snapPoint };
}

/**
 * Precompute interpolated path segments for a set of line series.
 * Reuse the returned index on each pointer move to avoid re-interpolating curves.
 *
 * @param {Object} config
 * @param {LineSeries[]} config.series
 * @param {Function} config.x
 * @param {Function} config.y
 * @param {import('d3-shape').CurveFactory | import('d3-shape').CurveFactoryLineOnly} [config.curve]
 * @param {Function} [config.defined]
 * @returns {LineProximityIndex}
 */
export function buildLineProximityIndex({ series = [], x, y, curve, defined }) {
  return {
    entries: series
      .filter((entry) => entry?.data?.length)
      .map((entry) => {
        const segments = collectLinePathSegments({
          data: entry.data,
          x,
          y,
          curve,
          defined,
        });

        return {
          id: entry.id,
          series: entry,
          segments,
          points:
            segments.length === 0
              ? collectDefinedDataPoints({
                  data: entry.data,
                  x,
                  y,
                  defined,
                })
              : [],
        };
      }),
  };
}

/**
 * Find the closest line in a precomputed proximity index.
 *
 * @param {LineProximityIndex} index
 * @param {ChartPoint} point
 * @param {Object} [options]
 * @param {number} [options.maxDistance=Infinity]
 * @param {number} [options.samplesPerSegment]
 * @returns {ClosestLineMatch | null}
 */
export function findClosestLineInIndex(
  index,
  point,
  {
    maxDistance = Infinity,
    samplesPerSegment = DEFAULT_SAMPLES_PER_SEGMENT,
  } = {}
) {
  if (!point || !index?.entries?.length) {
    return null;
  }

  let closestMatch = null;

  index.entries.forEach(({ id, series, segments, points }) => {
    const { distance, snapPoint } = distanceToPathSegments(
      point,
      segments,
      samplesPerSegment,
      points
    );

    if (distance > maxDistance || snapPoint == null) {
      return;
    }

    if (!closestMatch || distance < closestMatch.distance) {
      closestMatch = {
        id,
        series,
        distance,
        snapPoint,
      };
    }
  });

  return closestMatch;
}

/**
 * Compute the closest point on a single line path to a chart-space pointer.
 *
 * @param {Object} config
 * @param {Array<*>} config.data
 * @param {Function} config.x
 * @param {Function} config.y
 * @param {import('d3-shape').CurveFactory | import('d3-shape').CurveFactoryLineOnly} [config.curve]
 * @param {Function} [config.defined]
 * @param {ChartPoint} config.point
 * @param {number} [config.samplesPerSegment]
 * @returns {{ distance: number, snapPoint: ChartPoint | null }}
 */
export function distanceToLinePath({
  data,
  x,
  y,
  curve,
  defined,
  point,
  samplesPerSegment = DEFAULT_SAMPLES_PER_SEGMENT,
}) {
  const segments = collectLinePathSegments({ data, x, y, curve, defined });
  const points =
    segments.length === 0
      ? collectDefinedDataPoints({ data, x, y, defined })
      : [];

  return distanceToPathSegments(point, segments, samplesPerSegment, points);
}

/**
 * Find the closest line series to a pointer within an optional pixel threshold.
 *
 * @param {Object} config
 * @param {LineSeries[]} config.series
 * @param {ChartPoint} config.point chart-space coordinates (after margins)
 * @param {Function} config.x
 * @param {Function} config.y
 * @param {import('d3-shape').CurveFactory | import('d3-shape').CurveFactoryLineOnly} [config.curve]
 * @param {Function} [config.defined]
 * @param {number} [config.maxDistance=Infinity] minimum proximity in pixels; lines farther away are ignored
 * @param {number} [config.samplesPerSegment]
 * @returns {ClosestLineMatch | null}
 */
export function findClosestLineSeries({
  series = [],
  point,
  x,
  y,
  curve,
  defined,
  maxDistance = Infinity,
  samplesPerSegment = DEFAULT_SAMPLES_PER_SEGMENT,
}) {
  const index = buildLineProximityIndex({ series, x, y, curve, defined });

  return findClosestLineInIndex(index, point, {
    maxDistance,
    samplesPerSegment,
  });
}

/**
 * Resolve chart-space pointer coordinates from a mouse event.
 *
 * @param {Object} config
 * @param {MouseEvent | React.MouseEvent} config.event
 * @param {SVGElement} config.svgElement
 * @param {{ left?: number, top?: number }} [config.margin]
 * @returns {ChartPoint | null}
 */
export function getChartPointFromEvent({ event, svgElement, margin = {} }) {
  const coords = localPoint(svgElement ?? event.target?.ownerSVGElement, event);

  if (!coords) {
    return null;
  }

  return {
    x: coords.x - (margin.left ?? 0),
    y: coords.y - (margin.top ?? 0),
  };
}

/**
 * Convenience wrapper for chart onMouseMove handlers.
 *
 * @param {Object} config
 * @param {MouseEvent | React.MouseEvent} config.event
 * @param {SVGElement} config.svgElement
 * @param {{ left?: number, top?: number }} [config.margin]
 * @param {LineSeries[]} config.series
 * @param {Function} config.x
 * @param {Function} config.y
 * @param {import('d3-shape').CurveFactory | import('d3-shape').CurveFactoryLineOnly} [config.curve]
 * @param {Function} [config.defined]
 * @param {number} [config.maxDistance]
 * @param {number} [config.samplesPerSegment]
 * @returns {ClosestLineMatch | null}
 */
export function findActiveLineFromEvent({
  event,
  svgElement,
  margin,
  series,
  x,
  y,
  curve,
  defined,
  maxDistance,
  samplesPerSegment,
  proximityIndex,
}) {
  const point = getChartPointFromEvent({ event, svgElement, margin });

  if (!point) {
    return null;
  }

  if (proximityIndex) {
    return findClosestLineInIndex(proximityIndex, point, {
      maxDistance,
      samplesPerSegment,
    });
  }

  return findClosestLineSeries({
    series,
    point,
    x,
    y,
    curve,
    defined,
    maxDistance,
    samplesPerSegment,
  });
}
